import Fuse from 'fuse.js'
import { format, parse, addMonths, subMonths, getMonth, formatISO } from 'date-fns'
import { latinize } from 'modern-diacritics'
import { uniq, isEqual, initial } from 'lodash'

export function search(needle, haystack, keys = [], results = 3, options = {}) {
  const fuse = new Fuse(haystack, {
    keys,
    threshold: 0.2,
    ...options
  })

  return fuse.search(needle)
    .map(hit => hit.item)
    .slice(0, results)
}

// This will format money like $206,000,000.00
export function formatMoney(amount) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  })

  return formatter.format(amount)
}

// This will return compact version like $206M
export function formatMoneyCompact(amount) {
  const formatter = Intl.NumberFormat("en", {
    notation: "compact",
    style: 'currency',
    currency: 'USD'
  });

  return formatter.format(amount)
}

/*
  This function takes one of 3 date formats we get back from SFDC and converts
  them into an ISO 8601 format ie... 2023-06-01
*/
export function prettyDate(date) {
  // Capturing NULL values received
  if (date === null) {
    console.warn('Date received in prettyDate function inside src/js/utils.js is `NULL`.')
    return ''
  }

  // Date string is in ISO STRING format ( ex... 2022-08-22T00:00:00.000+0000 )
  if (date.length > 10) {
    return formatISO(new Date(date), {representation: 'date'})
  }

  // Date string is in OBJECT format ( ex.... Wed Oct 08 2022 00:00:00 GTM-0400 )
  else if(typeof date === 'object') {
    return format(new Date(date),'yyyy-MM-dd')
  }

  // Date string is already in correct format ( ex... 2022-08-22 )
  else {
    return date
  }
}

/*
  This function takes an ISO 8601 formatted date and returns Date String
*/
export function prettyDateToDateString(date) {
  // Capturing NULL values received
  if (date === null) {
    console.error('Date received in prettyDateToDateString function inside src/js/utils.js is `NULL`.')
    return ''
  }

  // If date is already in a Date String, just return the Date
  if (typeof date === 'object' && date !== null) {
    return date
  }

  // Returns Date from 2023-06-01 to Date String like Thur June 01 2023 00:00:00 GTM-0400
  return new Date(parse(date, 'yyyy-MM-dd', new Date()))
}


/**
 * A functional approach to "match expressions". It uses syntax similar to
 * a switch statement, but allows for return values to perform assignments
 * conditionally.
 *
 * Example:
 *
 * const isFruit = match('apple', {
 *   apple: true,         // isFruit === true
 *   orange: true,
 *   potato: false,
 *   onion: () => false   // You can use functions!
 * })
 *
 * @param {*} value
 * @param {*} lookup
 * @param  {...any} args
 */
export function match(value, lookup, ...args) {
  if (value in lookup) {
    const ret = lookup[value]
    return typeof ret === 'function' ? ret(...args) : ret
  }

  if (lookup.default) {
    return typeof lookup.default === 'function' ? lookup.default(...args) : lookup.default
  }

  const error = new Error(
    `Tried to handle "${value}" but there is no handler defined. Only defined handlers are: ${Object.keys(lookup).map(key => `"${key}"`).join(', ')}`
  )

  if (Error.captureStackTrace) {
    Error.captureStackTrace(error, match)
  }

  throw error
}

export const quarter = {
  fromDate(date) {
    date = new Date(date)

    // MONTHS ARE ZERO INDEXED WITH DATE-FNS
    const q = [2,3,3,3,4,4,4,1,1,1,2,2][getMonth(date)]
    const fy = getMonth(addMonths(date, 1)) >= 8 || getMonth(addMonths(date, 1)) == 0 ? Number(format(date, 'yy')) + 1 : Number(format(date, 'yy'))

    return [q, fy]
  },

  toDate(quarter) {
    let q, fy

    if (typeof quarter === 'string') {
      [, q, fy] = quarter.match(/Q(\d)FY(\d\d)/)
    } else if (Array.isArray(quarter) && quarter.length === 2) {
      [q, fy] = quarter
    }

    if (q < 1 || q > 4) {
      return
    }

    const month = [8, 11, 2, 5][q - 1]
    const year = month + 1 >= 8 ? parseInt(fy) - 1 : fy

    return format(new Date(`${month}/01/${year}`), 'yyyy-MM-dd')
  },

  startDate(date) {
    return this.toDate(this.fromDate(date))
  },

  next(count = 1) {

    return  [...Array(count).keys()].map(offset => {
      const quarter = this.fromDate(addMonths(new Date(), offset * 3 ))

      return {
        quarter,
        startDate: this.toDate(quarter)
      }
    })
  },

  previous(count = 1) {

    return  [...Array(count).keys()].map(offset => {
      const quarter = this.fromDate(subMonths(new Date(), offset * 3 ))

      return {
        quarter,
        startDate: this.toDate(quarter)
      }
    })
  },

  formattedString(date) {
    let vals = []
    let result = ''
    try {
      vals = quarter.fromDate(date)

      if (vals.length > 0) {
        result = `Q${vals[0]} FY${vals[1]}`
      }
    }
    catch(err) {
      console.error(err)
    }
    finally {
      return result
    }
  }
}

/*
  This function takes an attachment name test_attachment.doc and maps through
  an extension object to find appropriate icon from Iconify and returns
  to the frontend.
*/
export function attachmentIcon(attachmentName) {
  let icons = {
    default: 'guidance:paper',
    xls: 'vscode-icons:file-type-excel',
    pdf: 'vscode-icons:file-type-pdf2',
    ppt: 'vscode-icons:file-type-powerpoint',
    doc: 'vscode-icons:file-type-word',
    txt: 'fxemoji:documentwithtext',
    jpg: 'fxemoji:documentwithpicture',
    png: 'iwwa:file-png',
    zip: 'vscode-icons:file-type-zip',
    ics: 'flat-color-icons:electronics',
    svg: 'skill-icons:svg-light',
    csv: 'catppuccin:csv',
  }

  // Take document name and capture characters after last period (ie... '.pdf') returns 'pdf'
  let attachmentType = attachmentName.split('.').pop()

  // If attachmentType has an 'X' at the end (ie... 'xlsx') is converted to 'xls'
  if (attachmentType.slice(-1) === 'x') {
    attachmentType = attachmentType.slice(0,-1)
  }

  return icons[attachmentType] || icons.default
}

/*
  This function normalizes text by removing accents (diacritics) from strings to latin characters
  ie. 'ỆᶍǍᶆṔƚÉ áéíóúýčďěňřšťžů' becomes 'ExAmPlE aeiouycdenrstzu'

  - we have contacts for example where names have accents and this normalizes it for filter/comparing
*/
export function normalizeText(text) {
  return latinize(text)
}

/*
  Determine if value is a primitive data type.
  ie... string, number, bigint, boolean, undefined, symbol, null
*/
export function isPrimitive(value) {
  return value === Object(value) ? false : true
}

/*
  Converts long strings into multiple lines.
  ie... use case exmaple like CSV export and text areas which are really long.
*/
export function convertLongStringIntoMultipleLines(string, chunkSize = 10) {
  if (!string) return

  let convertStringIntoArrayOfWords = string.split(' '),
    stringArrayIntoChunks = []

  while(convertStringIntoArrayOfWords.length > 0) {
    stringArrayIntoChunks.push(convertStringIntoArrayOfWords.splice(0, chunkSize))
  }

  return `${[...stringArrayIntoChunks].map(item => item.join(' ')).join('\n')}`
}

/*
  Throw an uncaught exception in the console if condition is false
*/
export function assert(condition, message) {
  if (!condition) {
    throw new Error(message || 'Assertion failed')
  }
}

/**
 * Convert multiple picklist array to string
*/
export function convertArrayToString(arr) {
  if (arr?.length > 0) {
    return arr.join(';')
  }
  return null
}

/**
 * Convert mutiple picklist string to an array
*/
export function convertStringToArray(string) {
  if (string) {
    return string.split(';')
  }
  return []
}

/**
 * Compare two objects for equality. Used in forms where we check if any of the
 * form controls have changed.
*/
export function checkFormForChanges(state, initialState, changedFields, formDataHasChanged) {
  if (state !== null) {
    const data = uniq([...Object.keys(state), ...Object.keys(initialState)])

    for(const key of data){
      // If type == string, this would replace any new line or spaces for comparison purposes
      if (typeof state[key] === 'string') {
        const normalize = (text) => text !== null ? text.replace(/\r\n/g, '\n').trim() : null
        let tempState = normalize(state[key])
        let tempInitialState = normalize(initialState[key])

        // This code is duplicated specifically so that we don't cause weird backspacing behavior
        // inside of the form control. It allows us to compare without mutating the current state
        // of the text area inputs through reassignment.
        if (!isEqual(tempState, tempInitialState)) {
          if (!changedFields.value.includes(key)) {
            changedFields.value.push(key)
          }
        }
        else {
          changedFields.value = changedFields.value.filter(v => v !== key)
        }
      }
      else {
        // Check if state and initialState are equal
        if(!isEqual(state[key], initialState[key])){
          if (!changedFields.value.includes(key)) {
            changedFields.value.push(key)
          }
        }
        else {
          changedFields.value = changedFields.value.filter(v => v !== key)
        }
      }
    }
  changedFields.value.length ? formDataHasChanged.value = true : formDataHasChanged.value = false
  }
}